import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { CheckCircle, Download, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'react-hot-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BillingHistory = ({ userId }) => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingInvoice, setDownloadingInvoice] = useState(null);

  useEffect(() => {
    if (userId) {
      fetchTransactions();
    }
  }, [userId]);

  const fetchTransactions = async () => {
    try {
      const response = await axios.get(`${API}/billing/transactions`);
      setTransactions(response.data.transactions || []);
    } catch (error) {
      console.error('Error fetching transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadInvoice = async (sessionId, planName) => {
    setDownloadingInvoice(sessionId);
    try {
      const response = await axios.get(`${API}/billing/invoice/${sessionId}`);
      
      if (response.data.type === 'stripe_receipt' && response.data.invoice_url) {
        // Open Stripe receipt in new tab
        window.open(response.data.invoice_url, '_blank');
      } else if (response.data.type === 'generated' && response.data.invoice_data) {
        // Generate and download PDF invoice
        const invoice = response.data.invoice_data;
        generateInvoicePDF(invoice);
      }
    } catch (error) {
      console.error('Error downloading invoice:', error);
      toast.error('Failed to download invoice');
    } finally {
      setDownloadingInvoice(null);
    }
  };

  const generateInvoicePDF = (invoice) => {
    // Create a printable invoice HTML and open in new window
    const invoiceHTML = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Invoice ${invoice.invoice_number}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
          .header { display: flex; justify-content: space-between; margin-bottom: 40px; }
          .company { text-align: right; }
          .invoice-title { font-size: 32px; color: #D4AF37; margin-bottom: 10px; }
          .invoice-number { color: #666; margin-bottom: 20px; }
          .section { margin-bottom: 30px; }
          .section-title { font-weight: bold; color: #333; margin-bottom: 10px; border-bottom: 1px solid #ddd; padding-bottom: 5px; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
          th, td { padding: 12px; text-align: left; border-bottom: 1px solid #ddd; }
          th { background-color: #f5f5f5; }
          .total-row { font-weight: bold; font-size: 18px; }
          .total-row td { border-top: 2px solid #333; }
          .status { display: inline-block; padding: 4px 12px; background: #4CAF50; color: white; border-radius: 4px; }
          .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #ddd; color: #666; font-size: 12px; }
          @media print { body { padding: 20px; } }
        </style>
      </head>
      <body>
        <div class="header">
          <div>
            <div class="invoice-title">INVOICE</div>
            <div class="invoice-number">${invoice.invoice_number}</div>
            <div>Date: ${new Date(invoice.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</div>
            <div style="margin-top: 10px;"><span class="status">${invoice.status}</span></div>
          </div>
          <div class="company">
            <strong>${invoice.company.name}</strong><br>
            ${invoice.company.address}
          </div>
        </div>
        
        <div class="section">
          <div class="section-title">Bill To</div>
          <div>${invoice.customer.name}</div>
          <div>${invoice.customer.email}</div>
        </div>
        
        <div class="section">
          <div class="section-title">Items</div>
          <table>
            <thead>
              <tr>
                <th>Description</th>
                <th style="text-align: right;">Amount</th>
              </tr>
            </thead>
            <tbody>
              ${invoice.items.map(item => `
                <tr>
                  <td>${item.description}</td>
                  <td style="text-align: right;">£${item.amount.toFixed(2)}</td>
                </tr>
              `).join('')}
              <tr class="total-row">
                <td>Total</td>
                <td style="text-align: right;">£${invoice.total.toFixed(2)} ${invoice.currency}</td>
              </tr>
            </tbody>
          </table>
        </div>
        
        <div class="footer">
          <p>Thank you for your business!</p>
          <p>Growth & Brains Ltd - Lawra AI Legal Assistant</p>
        </div>
        
        <script>window.print();</script>
      </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(invoiceHTML);
    printWindow.document.close();
  };

  if (loading) {
    return (
      <div className="text-center py-4" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
        Loading payment history...
      </div>
    );
  }

  if (transactions.length === 0) {
    return (
      <div className="text-center py-8" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
        <p>No payment history yet</p>
        <p className="text-sm mt-2">Your completed transactions will appear here</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {transactions.map((transaction) => (
        <div
          key={transaction.session_id}
          className="bg-[rgba(20,20,25,0.6)] p-4 rounded-lg border border-[rgba(212,175,55,0.2)] hover:border-[rgba(212,175,55,0.4)] transition-colors"
        >
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <CheckCircle size={20} className="text-[#4CAF50]" />
                <div>
                  <h5 className="text-white font-semibold" style={{ fontSize: '1rem' }}>
                    {transaction.plan_name}
                  </h5>
                  <p className="text-sm" style={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                    {new Date(transaction.created_at).toLocaleDateString('en-GB', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-4 mt-2">
                <span className="text-sm" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  {transaction.credits} credits
                </span>
                <span className="text-sm px-2 py-1 rounded" 
                  style={{ 
                    backgroundColor: 'rgba(76, 175, 80, 0.2)',
                    color: '#4CAF50'
                  }}
                >
                  Completed
                </span>
              </div>
            </div>
            <div className="text-right flex flex-col items-end gap-2">
              <p className="text-xl font-bold text-[#D4AF37]">
                £{transaction.amount.toFixed(2)}
              </p>
              <Button
                variant="outline"
                size="sm"
                className="border-[rgba(212,175,55,0.5)] text-[#D4AF37] hover:bg-[rgba(212,175,55,0.1)] text-xs"
                onClick={() => handleDownloadInvoice(transaction.session_id, transaction.plan_name)}
                disabled={downloadingInvoice === transaction.session_id}
              >
                <FileText size={14} className="mr-1" />
                {downloadingInvoice === transaction.session_id ? 'Loading...' : 'Invoice'}
              </Button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default BillingHistory;
